import React, { useEffect, useMemo, useState } from 'react';
import { api } from '@/lib/api';
import { Panel } from '@/components/Panel';
import { toast } from 'sonner';
import { Plus, Trash2, Edit3, Copy, X, Save, Link as LinkIcon, ExternalLink } from 'lucide-react';

const empty = () => ({
  name: '', email: '', bio: '', avatar_url: '',
  referral_code: '', commission_percent: 10, discount_percent: 15, active: true,
  socials: { instagram: '', tiktok: '', website: '' },
});

function Input(props) {
  return <input {...props} className={`w-full rounded-md border px-3 py-2 text-sm bg-white ${props.className || ''}`}
    style={{ borderColor: 'rgba(75,24,56,0.25)', color: 'var(--heritage-plum)' }} />;
}
function Field({ label, hint, children }) {
  return <label className="block"><div className="h-eyebrow mb-1.5">{label}</div>{children}
    {hint && <div className="text-[11px] opacity-70 mt-1" style={{ color: 'var(--heritage-plum)' }}>{hint}</div>}</label>;
}

function Editor({ initial, onClose, onSaved }) {
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [links, setLinks] = useState(null); // { signup_link, referral_link }
  const isNew = !form.id;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setSocial = (k, v) => setForm((f) => ({ ...f, socials: { ...(f.socials || {}), [k]: v } }));

  const save = async () => {
    if (!form.name || !form.email || !form.referral_code) { toast.error('Name, email + referral code required'); return; }
    setSaving(true);
    try {
      const payload = {
        name: form.name, email: form.email, bio: form.bio || '', avatar_url: form.avatar_url || '',
        referral_code: (form.referral_code || '').toUpperCase().replace(/\s+/g, ''),
        commission_percent: Number(form.commission_percent) || 0,
        discount_percent: Number(form.discount_percent) || 0,
        active: !!form.active, socials: form.socials || {},
      };
      if (isNew) {
        const { data } = await api.post('/admin/ambassadors', payload);
        setLinks({ signup_link: data.signup_link, referral_link: `/ambassador/${payload.referral_code}` });
        toast.success('Ambassador created');
      } else {
        await api.put(`/admin/ambassadors/${form.id}`, payload);
        toast.success('Saved');
        onSaved();
      }
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); } finally { setSaving(false); }
  };

  const copy = async (val) => { await navigator.clipboard.writeText(val); toast.success('Copied'); };

  return (
    <div className="fixed inset-0 z-50 flex items-start md:items-center justify-center p-3" style={{ background: 'rgba(75,24,56,0.55)' }} onClick={onClose}>
      <div className="w-full max-w-2xl rounded-2xl overflow-hidden max-h-[92vh] flex flex-col" style={{ background: '#FBF8F1', border: '3px solid var(--heritage-plum)' }} onClick={(e) => e.stopPropagation()} data-testid="amb-editor">
        <div className="px-6 py-4 border-b flex items-center justify-between" style={{ borderColor: 'rgba(75,24,56,0.15)' }}>
          <div><div className="h-eyebrow">{isNew ? 'New ambassador' : 'Edit ambassador'}</div><div className="h-display text-2xl mt-0.5" style={{ color: 'var(--heritage-plum)' }}>{form.name || 'Untitled'}</div></div>
          <button onClick={onClose} className="p-1" aria-label="Close" style={{ color: 'var(--heritage-plum)' }}><X size={20} /></button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Full name"><Input value={form.name} onChange={(e) => set('name', e.target.value)} data-testid="amb-name" /></Field>
            <Field label="Email"><Input type="email" value={form.email} onChange={(e) => set('email', e.target.value)} data-testid="amb-email" /></Field>
            <Field label="Referral code" hint="Used as their unique link + discount code"><Input value={form.referral_code} onChange={(e) => set('referral_code', e.target.value.toUpperCase().replace(/\s+/g, ''))} placeholder="KAYLA" data-testid="amb-code" /></Field>
            <Field label="Commission %" hint="They earn this % of every sale via their code"><Input type="number" value={form.commission_percent} onChange={(e) => set('commission_percent', e.target.value)} data-testid="amb-commission" /></Field>
            <Field label="Discount % (to followers)" hint="Their code gives shoppers this % off"><Input type="number" value={form.discount_percent} onChange={(e) => set('discount_percent', e.target.value)} data-testid="amb-discount" /></Field>
            <Field label="Status"><select value={form.active ? '1' : '0'} onChange={(e) => set('active', e.target.value === '1')} className="w-full rounded-md border px-3 py-2 text-sm bg-white" style={{ borderColor: 'rgba(75,24,56,0.25)', color: 'var(--heritage-plum)' }}><option value="1">Active</option><option value="0">Paused</option></select></Field>
          </div>
          <Field label="Avatar image URL"><Input value={form.avatar_url} onChange={(e) => set('avatar_url', e.target.value)} placeholder="https://..." /></Field>
          <Field label="Bio"><textarea rows={3} value={form.bio} onChange={(e) => set('bio', e.target.value)} className="w-full rounded-md border px-3 py-2 text-sm bg-white" style={{ borderColor: 'rgba(75,24,56,0.25)', color: 'var(--heritage-plum)' }} /></Field>
          <div className="grid md:grid-cols-3 gap-3">
            <Field label="Instagram"><Input value={(form.socials || {}).instagram || ''} onChange={(e) => setSocial('instagram', e.target.value)} placeholder="@handle" /></Field>
            <Field label="TikTok"><Input value={(form.socials || {}).tiktok || ''} onChange={(e) => setSocial('tiktok', e.target.value)} placeholder="@handle" /></Field>
            <Field label="Website"><Input value={(form.socials || {}).website || ''} onChange={(e) => setSocial('website', e.target.value)} placeholder="https://" /></Field>
          </div>

          {links && (
            <div className="space-y-2 rounded-md p-3" style={{ background: 'rgba(91,110,44,0.1)' }} data-testid="amb-links-panel">
              <div className="h-eyebrow text-[10px]">Share these with your ambassador</div>
              {links.signup_link && (
                <div className="flex items-center justify-between gap-2 rounded-md p-2" style={{ background: '#FBF8F1' }}>
                  <div className="min-w-0"><div className="text-[10px] uppercase tracking-widest opacity-70" style={{ color: 'var(--heritage-plum)' }}>Signup link (single-use)</div>
                    <div className="text-xs break-all" style={{ color: 'var(--heritage-plum)' }}>{links.signup_link}</div></div>
                  <button onClick={() => copy(links.signup_link)} className="h-btn-outline text-xs" data-testid="copy-signup"><Copy size={12} /></button>
                </div>
              )}
              <div className="flex items-center justify-between gap-2 rounded-md p-2" style={{ background: '#FBF8F1' }}>
                <div className="min-w-0"><div className="text-[10px] uppercase tracking-widest opacity-70" style={{ color: 'var(--heritage-plum)' }}>Referral link</div>
                  <div className="text-xs break-all" style={{ color: 'var(--heritage-plum)' }}>{window.location.origin}{links.referral_link}</div></div>
                <button onClick={() => copy(`${window.location.origin}${links.referral_link}`)} className="h-btn-outline text-xs" data-testid="copy-referral"><Copy size={12} /></button>
              </div>
              <div className="flex justify-end"><button onClick={onSaved} className="h-btn text-xs">Done</button></div>
            </div>
          )}
        </div>

        {!links && (
          <div className="px-6 py-4 border-t flex items-center justify-end gap-2" style={{ borderColor: 'rgba(75,24,56,0.15)' }}>
            <button onClick={onClose} className="h-btn-outline text-xs">Cancel</button>
            <button onClick={save} disabled={saving} className="h-btn text-xs" data-testid="amb-save"><Save size={12} className="mr-1" /> {saving ? 'Saving…' : (isNew ? 'Create ambassador' : 'Save changes')}</button>
          </div>
        )}
      </div>
    </div>
  );
}

export default function AdminAmbassadors() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [q, setQ] = useState('');

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get('/admin/ambassadors'); setRows(data.ambassadors || []); }
    catch { toast.error('Failed to load ambassadors'); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const s = q.toLowerCase().trim(); if (!s) return rows;
    return rows.filter((a) => (a.name || '').toLowerCase().includes(s) || (a.email || '').toLowerCase().includes(s) || (a.referral_code || '').toLowerCase().includes(s));
  }, [rows, q]);

  const remove = async (a) => {
    if (!window.confirm(`Delete ${a.name}? Their referral code + payout history will be removed. This cannot be undone.`)) return;
    try { await api.delete(`/admin/ambassadors/${a.id}`); toast.success('Deleted'); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  const shareLinks = async (a) => {
    try {
      const { data } = await api.get(`/admin/ambassadors/${a.id}/signup-link`);
      await navigator.clipboard.writeText(`Signup: ${data.signup_link}\nReferral: ${data.referral_link}`);
      toast.success('Signup + referral links copied');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  return (
    <div className="space-y-5" data-testid="admin-ambassadors">
      <div className="h-panel-thin p-6 flex items-center justify-between">
        <div><div className="h-eyebrow">Ambassadors</div>
          <div className="h-display text-4xl mt-1" style={{ color: 'var(--heritage-plum)' }}>Heritage Ambassadors</div>
          <p className="text-sm opacity-85 mt-1" style={{ color: 'var(--heritage-plum)' }}>Add ambassadors, share their signup + referral links, edit commission %.</p></div>
        <button onClick={() => setEditing(empty())} className="h-btn text-xs" data-testid="amb-new"><Plus size={12} className="mr-1" /> New ambassador</button>
      </div>

      <Panel className="p-3"><input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email, code" className="w-full max-w-md rounded-md border px-3 py-2 text-sm bg-white" style={{ borderColor: 'rgba(75,24,56,0.25)', color: 'var(--heritage-plum)' }} data-testid="amb-search" /></Panel>

      {loading && <div className="h-eyebrow">Loading…</div>}
      {!loading && filtered.length === 0 && <Panel className="p-8 text-center"><div className="h-serif text-xl" style={{ color: 'var(--heritage-plum)' }}>No ambassadors yet</div></Panel>}

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {filtered.map((a) => (
          <Panel key={a.id} className="p-4" data-testid={`amb-card-${a.referral_code}`}>
            <div className="flex items-start gap-3">
              <div className="w-14 h-14 rounded-full overflow-hidden flex-shrink-0" style={{ background: 'rgba(75,24,56,0.1)' }}>
                {a.avatar_url ? <img src={a.avatar_url} alt={a.name} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center h-serif text-lg" style={{ color: 'var(--heritage-plum)' }}>{(a.name || '?')[0]}</div>}
              </div>
              <div className="flex-1 min-w-0">
                <div className="h-serif text-lg truncate" style={{ color: 'var(--heritage-plum)' }}>{a.name}</div>
                <div className="text-xs opacity-75 truncate" style={{ color: 'var(--heritage-plum)' }}>{a.email}</div>
                <div className="h-eyebrow text-[10px] mt-1">CODE {a.referral_code} · {a.commission_percent}% commission</div>
              </div>
              <span className="h-chip text-[9px]" style={{ background: a.active ? 'rgba(91,110,44,0.18)' : 'rgba(154,44,44,0.15)', borderColor: 'transparent', color: 'var(--heritage-plum)' }}>{a.active ? 'ACTIVE' : 'PAUSED'}</span>
            </div>
            <div className="flex items-center justify-end gap-2 mt-3 pt-3 border-t" style={{ borderColor: 'rgba(75,24,56,0.15)' }}>
              <button onClick={() => shareLinks(a)} className="h-btn-outline text-xs" data-testid={`amb-links-${a.referral_code}`} title="Copy signup + referral links"><LinkIcon size={12} className="mr-1" /> Copy links</button>
              <a href={`/ambassador/${a.referral_code}`} target="_blank" rel="noreferrer" className="h-btn-outline text-xs" title="View landing page"><ExternalLink size={12} /></a>
              <button onClick={() => setEditing({ ...a, socials: a.socials || {} })} className="h-btn-outline text-xs"><Edit3 size={12} /></button>
              <button onClick={() => remove(a)} className="text-xs inline-flex items-center gap-1 opacity-80 hover:opacity-100 px-2 py-1" style={{ color: '#9A2C2C' }} data-testid={`amb-delete-${a.referral_code}`}><Trash2 size={12} /></button>
            </div>
          </Panel>
        ))}
      </div>

      {editing && <Editor initial={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}
